import type { Dimensions } from '@studio/js-utils/geometry';
import type { Device } from '@studio/models/device';
import { AspectRatio } from '@studio/models/video';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { GutterSize, VisibleColumns } from './constants.js';
import type { Layout, Slot } from './types.js';

/** @internal */
export const computeLayout = (
  clientRect: Dimensions,
  videoIds: Device['id'][]
): T_Maybe<Omit<Layout, 'arrangement'>> => {
  const videoCount = videoIds.length;

  // Are there any videos?
  if (!videoCount) {
    return undefined;
  }

  const { height: clientHeight, width: clientWidth } = clientRect;

  const rowCount = videoCount <= 2 ? 1 : 2;
  const columnCount = Math.ceil(videoCount / rowCount);
  const visibleColumns = Math.min(columnCount, VisibleColumns);

  const rowHeight = (clientHeight - (rowCount + 1) * GutterSize) / rowCount;
  const columnWidth = (clientWidth - (visibleColumns + 1) * GutterSize) / visibleColumns;

  const videoHeight = Math.min(rowHeight, columnWidth / AspectRatio);
  const videoWidth = videoHeight * AspectRatio;

  // If there are only one or two columns, center the videos horizontally.
  const originX =
    columnCount <= VisibleColumns
      ? (clientWidth - columnCount * videoWidth - (columnCount - 1) * GutterSize) / 2
      : GutterSize;

  const originY = (clientHeight - rowCount * videoHeight - (rowCount - 1) * GutterSize) / 2;

  const slots: Slot[] = [];

  for (let videoIndex = 0; videoIndex < videoCount; videoIndex++) {
    // Fill columns first
    const columnIndex = Math.floor(videoIndex / rowCount);
    const rowIndex = videoIndex % rowCount;

    slots.push({
      columnIndex,
      deviceId: videoIds[videoIndex]!,
      rowIndex,
      style: {
        height: `${videoHeight}px`,
        left: `${originX + columnIndex * (videoWidth + GutterSize)}px`,
        top: `${originY + rowIndex * (videoHeight + GutterSize)}px`,
        width: `${videoWidth}px`
      }
    });
  }

  return {
    columns: {
      count: columnCount,
      visible: visibleColumns,
      width: columnWidth
    },
    rows: {
      count: rowCount,
      height: rowHeight
    },
    slots
  };
};
